/**
 * `react-native-maps`, drawn by Leaflet + OpenStreetMap in the browser.
 *
 * ---------------------------------------------------------------------------
 * WHY LEAFLET AND NOT AN EMBED OR THE GOOGLE JS SDK
 * ---------------------------------------------------------------------------
 * The app's two maps are not decorations. `LocationScreen` is a pin-drop
 * picker - it needs pan, and it needs to be TOLD when the pan stops
 * (`onRegionChangeComplete`) so it can reverse-geocode the centre. A static
 * embed can do neither. The Google Maps JS SDK can, but wants an API key
 * with billing attached; Leaflet over OSM tiles does everything these two
 * screens use and wants nothing.
 *
 * Only the surface the app actually touches is implemented:
 *
 *   MapView   initialRegion, onRegionChangeComplete, onMapReady, mapPadding,
 *             style/pointerEvents, ref.animateToRegion(region, ms)
 *   Marker    coordinate, title, description, pinColor
 *
 * Everything else (`provider`, `showsMyLocationButton`, `toolbarEnabled`)
 * is accepted and ignored - those switches configure the Google engine the
 * web build does not run.
 *
 * `mapPadding` is honoured for real, not dropped: `LocationScreen` slides a
 * results sheet over the map's bottom and relies on the padded centre being
 * "the centre of what you can see". Both the reported region and
 * `animateToRegion`'s target run through the same padding offset.
 */

import React, {
  forwardRef,
  isValidElement,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
} from 'react';
import { View } from 'react-native-web';
import type { StyleProp, ViewStyle } from 'react-native';
// Order matters: the flags module must run before Leaflet evaluates.
import './leaflet-flags';
import * as L from 'leaflet';
import 'leaflet/dist/leaflet.css';

export type Region = {
  latitude: number;
  longitude: number;
  latitudeDelta: number;
  longitudeDelta: number;
};

type EdgePadding = { top: number; right: number; bottom: number; left: number };

type MarkerProps = {
  coordinate: { latitude: number; longitude: number };
  title?: string;
  description?: string;
  pinColor?: string;
};

export type MapViewProps = {
  provider?: string;
  /*
   * `unknown` until `react-native-web` had types, because nothing checked
   * it: the `View` it is handed through was itself `any`. It is the same
   * prop `react-native-maps` takes, so it says so now - and the moment it
   * did, TypeScript refused to pass an `unknown` on, which is exactly what
   * saying so is for.
   */
  style?: StyleProp<ViewStyle>;
  initialRegion?: Region;
  onRegionChangeComplete?: (region: Region) => void;
  onMapReady?: () => void;
  mapPadding?: EdgePadding;
  pointerEvents?: 'auto' | 'none' | 'box-none' | 'box-only';
  showsMyLocationButton?: boolean;
  toolbarEnabled?: boolean;
  children?: React.ReactNode;
};

/** Accepted for compatibility; the web build has exactly one engine. */
export const PROVIDER_GOOGLE = 'google';

const NO_PADDING: EdgePadding = { top: 0, right: 0, bottom: 0, left: 0 };

/** Web Mercator: the zoom at which `longitudeDelta` spans `widthPx`. */
const zoomFor = (region: Region, widthPx: number): number => {
  const width = Math.max(widthPx, 1);
  const zoom = Math.log2((360 * width) / (256 * region.longitudeDelta));
  return Math.min(19, Math.max(3, zoom));
};

const escapeHtml = (text: string): string =>
  text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

/**
 * The classic teardrop, coloured per marker.
 *
 * An inline SVG divIcon rather than Leaflet's default marker images, because
 * those load from paths a bundler has to be taught about - and because the
 * app colours its pins (`pinColor` is a real signal: the SOS map greys a
 * driver whose phone has gone quiet).
 */
const pinIcon = (color: string): L.DivIcon =>
  L.divIcon({
    className: '',
    html:
      `<svg width="30" height="42" viewBox="0 0 30 42" xmlns="http://www.w3.org/2000/svg">` +
      `<path d="M15 41C15 41 3 24.5 3 14.5C3 7.6 8.4 2 15 2C21.6 2 27 7.6 27 14.5C27 24.5 15 41 15 41Z"` +
      ` fill="${color}" stroke="#ffffff" stroke-width="2"/>` +
      `<circle cx="15" cy="14.5" r="4.5" fill="#ffffff"/></svg>`,
    iconSize: [30, 42],
    iconAnchor: [15, 40],
    popupAnchor: [0, -36],
  });

/** Declarative only - `MapView` reads its props; it renders nothing itself. */
export const Marker: React.FC<MarkerProps> = () => null;

const markerSpecsOf = (children: React.ReactNode): MarkerProps[] =>
  React.Children.toArray(children)
    .filter(
      (child): child is React.ReactElement<MarkerProps> =>
        isValidElement(child) && child.type === Marker,
    )
    .map(child => child.props);

export type MapViewHandle = {
  animateToRegion: (region: Region, durationMs?: number) => void;
};

const MapView = forwardRef<MapViewHandle, MapViewProps>(function MapView(
  props,
  ref,
) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<L.Map | null>(null);
  const markersRef = useRef<L.Marker[]>([]);

  /*
   * Read through refs from Leaflet's event handlers, so a parent re-render
   * with a fresh callback never needs the map torn down and rebuilt.
   */
  const paddingRef = useRef<EdgePadding>(props.mapPadding ?? NO_PADDING);
  paddingRef.current = props.mapPadding ?? NO_PADDING;
  const onRegionChangeCompleteRef = useRef(props.onRegionChangeComplete);
  onRegionChangeCompleteRef.current = props.onRegionChangeComplete;

  /** The centre of the UNCOVERED part of the map - see the header note. */
  const visibleRegion = (map: L.Map): Region => {
    const size = map.getSize();
    const pad = paddingRef.current;
    const centre = map.containerPointToLatLng([
      pad.left + (size.x - pad.left - pad.right) / 2,
      pad.top + (size.y - pad.top - pad.bottom) / 2,
    ]);
    const bounds = map.getBounds();
    return {
      latitude: centre.lat,
      longitude: centre.lng,
      latitudeDelta: bounds.getNorth() - bounds.getSouth(),
      longitudeDelta: bounds.getEast() - bounds.getWest(),
    };
  };

  /** Where the map's geometric centre must sit for `region`'s coordinate to
      land on the padded (visible) centre. */
  const paddedCentre = (
    map: L.Map,
    region: Region,
    zoom: number,
  ): L.LatLng => {
    const pad = paddingRef.current;
    const target = map.project([region.latitude, region.longitude], zoom);
    return map.unproject(
      target.add(
        L.point((pad.right - pad.left) / 2, (pad.bottom - pad.top) / 2),
      ),
      zoom,
    );
  };

  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container || mapRef.current) {
      return undefined;
    }

    const map = L.map(container, {
      // Pinch and scroll do the zooming, as on the phone. The +/- control
      // would sit exactly where LocationScreen floats its recentre button.
      zoomControl: false,
      attributionControl: true,
    });
    mapRef.current = map;

    L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
      attribution:
        '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
    }).addTo(map);

    const initial = props.initialRegion ?? {
      // Indore - the seed the app itself falls back to when nothing is known.
      latitude: 22.7196,
      longitude: 75.8577,
      latitudeDelta: 0.05,
      longitudeDelta: 0.05,
    };
    map.setView(
      [initial.latitude, initial.longitude],
      zoomFor(initial, container.clientWidth),
      { animate: false },
    );

    map.on('moveend', () => {
      onRegionChangeCompleteRef.current?.(visibleRegion(map));
    });

    map.whenReady(() => {
      props.onMapReady?.();
    });

    // A map mounted into a container that is still laying out renders one
    // grey tile; watching the box beats guessing when layout settles.
    const observer = new ResizeObserver(() => {
      map.invalidateSize({ animate: false });
    });
    observer.observe(container);

    return () => {
      observer.disconnect();
      markersRef.current = [];
      map.remove();
      mapRef.current = null;
    };
    // Mount-only by design: everything dynamic goes through refs above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useImperativeHandle(
    ref,
    () => ({
      animateToRegion: (region: Region, durationMs = 500) => {
        const map = mapRef.current;
        const container = containerRef.current;
        if (!map || !container) {
          return;
        }
        const zoom = zoomFor(region, container.clientWidth);
        map.flyTo(paddedCentre(map, region, zoom), zoom, {
          duration: Math.max(durationMs, 1) / 1000,
        });
      },
    }),
    [],
  );

  /*
   * Markers re-synced whenever their props change. Rebuilt wholesale rather
   * than diffed - the app's busiest map draws two.
   */
  const specs = markerSpecsOf(props.children);
  const specsKey = JSON.stringify(specs);
  useEffect(() => {
    const map = mapRef.current;
    if (!map) {
      return undefined;
    }

    markersRef.current = specs.map(spec => {
      const marker = L.marker(
        [spec.coordinate.latitude, spec.coordinate.longitude],
        { icon: pinIcon(spec.pinColor ?? '#1D4E35') },
      ).addTo(map);

      if (spec.title || spec.description) {
        marker.bindPopup(
          [
            spec.title ? `<b>${escapeHtml(spec.title)}</b>` : '',
            spec.description ? escapeHtml(spec.description) : '',
          ]
            .filter(Boolean)
            .join('<br/>'),
        );
      }
      return marker;
    });

    return () => {
      markersRef.current.forEach(marker => marker.remove());
      markersRef.current = [];
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [specsKey]);

  return (
    <View style={props.style} pointerEvents={props.pointerEvents}>
      {/*
        `zIndex: 0` + `isolation` pin Leaflet's own stacking (its panes run
        z-index 400-700) inside this box. Without them the tiles paint OVER
        siblings drawn after the map - LocationScreen's whole address sheet
        was underneath the city, clickable and invisible.
      */}
      <div
        ref={containerRef}
        style={{
          position: 'absolute',
          top: 0,
          right: 0,
          bottom: 0,
          left: 0,
          zIndex: 0,
          isolation: 'isolate',
        }}
      />
    </View>
  );
});

export default MapView;
